import isEmpty from "lodash/isEmpty";

import convertUnit from "utils/convertUnit";

import frameMaterialLogistic from "utils/enclosure/frame_material_logistic.json";

// -----------------------------------------------------------------------------
// Frame material (user-defined window overall) — NRCan ENERGY STARlogistic model
// -----------------------------------------------------------------------------

/** udefWindowOverallStyle id (0..8) → "Style Consolidated" labels from training. */
export const UDEF_WINDOW_STYLE_ID_TO_CONSOLIDATED = {
    0: "Awning",
    1: "Casement",
    2: "Picture",
    3: "Single Hung",
    4: "Double Hung",
    5: "Single Slider",
    6: "Double Slider",
    7: "Triple Slider",
    8: "Hopper",
};

/**
 * udefWindowOverallGlazingType id → "Layers of Glazing" (integer) for the NRCan model.
 * (Training data used mostly 2 and 3.)
 */
export const UDEF_GLAZING_TYPE_ID_TO_LAYERS = {
    0: 1,
    1: 2,
    2: 3,
    3: 3,
    4: 2,
    5: 2,
    6: 2,
};

function softmax(logits) {
    const m = Math.max(...logits);
    const e = logits.map((z) => Math.exp(z - m));
    const s = e.reduce((a, b) => a + b, 0);
    return e.map((v) => v / s);
}

/**
 * @param {object} overallThermalResistance
 * @param {0|1} [overallThermalResistance.id]
 * @param {number} [overallThermalResistance.value]
 * @param {string} [resistanceUnit] thermalResistance unit when `id` is 0: "RSI" or "R"
 * @returns {number|null} whole-window U-factor in W/m² K, or null if not computable
 */
export function getWindowOverallUValueMetric(overallThermalResistance, resistanceUnit) {
    const { id, value = 0 } = overallThermalResistance || {};
    if (id === 1) {
        if (!Number.isFinite(value) || value <= 0) return null;
        return value;
    }
    if (id === 0) {
        if (!Number.isFinite(value) || value <= 0) return null;
        let rsi;
        if (resistanceUnit === "R") {
            rsi = convertUnit({ value, type: "thermalResistance", inputUnit: "R", outputUnit: "RSI" });
        } else {
            // "RSI" and unknown fall back to treating stored value as RSI
            rsi = value;
        }
        if (!Number.isFinite(rsi) || rsi <= 0) return null;
        return 1 / rsi;
    }
    return null;
}

/**
 * @param {object} args
 * @param {number} [args.uMetric] W/m² K
 * @param {number} [args.shgc] 0..1
 * @param {number} [args.layersOfGlazing] integer, typically 1..4
 * @param {string} [args.styleConsolidated] e.g. "Casement" — must match `frame_material_logistic` ohe.categories, or is treated as unknown (all zeros)
 * @param {object} [args.model] optional override (for tests)
 * @returns {string|null} predicted `csWindowFrameMaterial` `value` string, or null
 */
export function predictFrameMaterialFromLogisticModel({
    uMetric,
    shgc,
    layersOfGlazing,
    styleConsolidated,
    model = frameMaterialLogistic,
} = {}) {
    if (model == null || !model.logistic) return null;
    if (uMetric == null || !Number.isFinite(uMetric) || uMetric <= 0) return null;
    if (shgc == null || !Number.isFinite(shgc)) return null;
    if (layersOfGlazing == null || !Number.isFinite(layersOfGlazing)) return null;

    const m = model.scaler;
    const num = [uMetric, shgc, layersOfGlazing];
    const nfeat = m.mean.length;
    if (nfeat !== 3) return null;

    const nScaled = num.map((v, i) => (v - m.mean[i]) / (m.scale[i] || 1));
    const cats = model.ohe?.categories || [];
    const ohe = new Array(cats.length).fill(0);
    if (styleConsolidated && cats.length) {
        const ix = cats.indexOf(styleConsolidated);
        if (ix >= 0) ohe[ix] = 1;
    }
    const x = nScaled.concat(ohe);
    const { coef, intercept } = model.logistic;
    if (!Array.isArray(coef) || !Array.isArray(intercept)) return null;
    if (x.length !== (coef[0] || []).length) return null;

    const logits = intercept.map((b, i) => coef[i].reduce((s, c, j) => s + c * (x[j] || 0), 0) + b);
    const p = softmax(logits);
    let j = 0;
    for (let i = 1; i < p.length; i += 1) {
        if (p[i] > p[j]) j = i;
    }
    const outClass = (model.classes || [])[j];
    return outClass != null ? String(outClass) : null;
}

export const getAllWindowAccessors = (components, accessorIgnore = "") => {
    const { wall = {}, ceiling = {}, basement = {}, crawlspace = {} } = components;

    const windowIgnore = accessorIgnore.split(".window.")[1];

    let accessorsToUpdate = [];
    if (!isEmpty(wall)) {
        Object.entries(wall).forEach(([wallKey, wallObj]) => {
            const { subcomponents: { window = {}, door = {} } = {} } = wallObj;

            if (!isEmpty(window)) {
                Object.keys(window).forEach((windowKey) => {
                    if (windowKey !== windowIgnore) {
                        accessorsToUpdate = [
                            ...accessorsToUpdate,
                            `model.modelData.components.wall.${wallKey}.subcomponents.window.${windowKey}`,
                        ];
                    }
                });
            }

            if (!isEmpty(door)) {
                Object.keys(door).forEach((doorKey) => {
                    const { subcomponents: { window: doorWindow = {} } = {} } = door[doorKey];
                    if (!isEmpty(doorWindow)) {
                        Object.keys(doorWindow).forEach((windowKey) => {
                            if (windowKey !== windowIgnore) {
                                accessorsToUpdate = [
                                    ...accessorsToUpdate,
                                    `model.modelData.components.wall.${wallKey}.subcomponents.door.${doorKey}.subcomponents.window.${windowKey}`,
                                ];
                            }
                        });
                    }
                });
            }
        });
    }

    if (!isEmpty(ceiling)) {
        Object.entries(ceiling).forEach(([ceilingKey, ceilingObj]) => {
            const { subcomponents: { window = {} } = {} } = ceilingObj;

            if (!isEmpty(window)) {
                Object.keys(window).forEach((windowKey) => {
                    if (windowKey !== windowIgnore) {
                        accessorsToUpdate = [
                            ...accessorsToUpdate,
                            `model.modelData.components.ceiling.${ceilingKey}.subcomponents.window.${windowKey}`,
                        ];
                    }
                });
            }
        });
    }

    if (!isEmpty(basement)) {
        Object.entries(basement).forEach(([basementKey, basementObj]) => {
            const { subcomponents: { window = {}, door = {} } = {} } = basementObj;

            if (!isEmpty(window)) {
                Object.keys(window).forEach((windowKey) => {
                    if (windowKey !== windowIgnore) {
                        accessorsToUpdate = [
                            ...accessorsToUpdate,
                            `model.modelData.components.basement.${basementKey}.subcomponents.window.${windowKey}`,
                        ];
                    }
                });
            }

            if (!isEmpty(door)) {
                Object.keys(door).forEach((doorKey) => {
                    const { subcomponents: { window: doorWindow = {} } = {} } = door[doorKey];
                    if (!isEmpty(doorWindow)) {
                        Object.keys(doorWindow).forEach((windowKey) => {
                            if (windowKey !== windowIgnore) {
                                accessorsToUpdate = [
                                    ...accessorsToUpdate,
                                    `model.modelData.components.basement.${basementKey}.subcomponents.door.${doorKey}.subcomponents.window.${windowKey}`,
                                ];
                            }
                        });
                    }
                });
            }
        });
    }

    if (!isEmpty(crawlspace)) {
        Object.entries(crawlspace).forEach(([crawlspaceKey, crawlspaceObj]) => {
            const { subcomponents: { window = {}, door = {} } = {} } = crawlspaceObj;

            if (!isEmpty(window)) {
                Object.keys(window).forEach((windowKey) => {
                    if (windowKey !== windowIgnore) {
                        accessorsToUpdate = [
                            ...accessorsToUpdate,
                            `model.modelData.components.crawlspace.${crawlspaceKey}.subcomponents.window.${windowKey}`,
                        ];
                    }
                });
            }

            if (!isEmpty(door)) {
                Object.keys(door).forEach((doorKey) => {
                    const { subcomponents: { window: doorWindow = {} } = {} } = door[doorKey];
                    if (!isEmpty(doorWindow)) {
                        Object.keys(doorWindow).forEach((windowKey) => {
                            if (windowKey !== windowIgnore) {
                                accessorsToUpdate = [
                                    ...accessorsToUpdate,
                                    `model.modelData.components.crawlspace.${crawlspaceKey}.subcomponents.door.${doorKey}.subcomponents.window.${windowKey}`,
                                ];
                            }
                        });
                    }
                });
            }
        });
    }

    return accessorsToUpdate;
};

export const getMatchingWindowCodeRefs = (components, editingCodeRef, accessorIgnore = "") => {
    const { wall = {}, ceiling = {}, basement = {}, crawlspace = {} } = components;

    const windowIgnore = accessorIgnore.split(".window.")[1];

    let accessorsToUpdate = [];
    if (!isEmpty(wall)) {
        Object.entries(wall).forEach(([wallKey, wallObj]) => {
            const { subcomponents: { window = {}, door = {} } = {} } = wallObj;

            if (!isEmpty(window)) {
                Object.keys(window).forEach((windowKey) => {
                    const { windowType: { codeRef: windowCodeRef = "" } = {} } = window[windowKey];

                    if (windowKey !== windowIgnore && windowCodeRef === editingCodeRef) {
                        accessorsToUpdate = [
                            ...accessorsToUpdate,
                            `model.modelData.components.wall.${wallKey}.subcomponents.window.${windowKey}`,
                        ];
                    }
                });
            }

            if (!isEmpty(door)) {
                Object.keys(door).forEach((doorKey) => {
                    const { subcomponents: { window: doorWindow = {} } = {} } = door[doorKey];
                    if (!isEmpty(doorWindow)) {
                        Object.keys(doorWindow).forEach((windowKey) => {
                            const { windowType: { codeRef: windowCodeRef = "" } = {} } = doorWindow[windowKey];

                            if (windowKey !== windowIgnore && windowCodeRef === editingCodeRef) {
                                accessorsToUpdate = [
                                    ...accessorsToUpdate,
                                    `model.modelData.components.wall.${wallKey}.subcomponents.door.${doorKey}.subcomponents.window.${windowKey}`,
                                ];
                            }
                        });
                    }
                });
            }
        });
    }

    if (!isEmpty(ceiling)) {
        Object.entries(ceiling).forEach(([ceilingKey, ceilingObj]) => {
            const { subcomponents: { window = {} } = {} } = ceilingObj;

            if (!isEmpty(window)) {
                Object.keys(window).forEach((windowKey) => {
                    const { windowType: { codeRef: windowCodeRef = "" } = {} } = window[windowKey];

                    if (windowKey !== windowIgnore && windowCodeRef === editingCodeRef) {
                        accessorsToUpdate = [
                            ...accessorsToUpdate,
                            `model.modelData.components.ceiling.${ceilingKey}.subcomponents.window.${windowKey}`,
                        ];
                    }
                });
            }
        });
    }

    if (!isEmpty(basement)) {
        Object.entries(basement).forEach(([basementKey, basementObj]) => {
            const { subcomponents: { window = {}, door = {} } = {} } = basementObj;

            if (!isEmpty(window)) {
                Object.keys(window).forEach((windowKey) => {
                    const { windowType: { codeRef: windowCodeRef = "" } = {} } = window[windowKey];

                    if (windowKey !== windowIgnore && windowCodeRef === editingCodeRef) {
                        accessorsToUpdate = [
                            ...accessorsToUpdate,
                            `model.modelData.components.basement.${basementKey}.subcomponents.window.${windowKey}`,
                        ];
                    }
                });
            }

            if (!isEmpty(door)) {
                Object.keys(door).forEach((doorKey) => {
                    const { subcomponents: { window: doorWindow = {} } = {} } = door[doorKey];
                    if (!isEmpty(doorWindow)) {
                        Object.keys(doorWindow).forEach((windowKey) => {
                            const { windowType: { codeRef: windowCodeRef = "" } = {} } = doorWindow[windowKey];

                            if (windowKey !== windowIgnore && windowCodeRef === editingCodeRef) {
                                accessorsToUpdate = [
                                    ...accessorsToUpdate,
                                    `model.modelData.components.basement.${basementKey}.subcomponents.door.${doorKey}.subcomponents.window.${windowKey}`,
                                ];
                            }
                        });
                    }
                });
            }
        });
    }

    if (!isEmpty(crawlspace)) {
        Object.entries(crawlspace).forEach(([crawlspaceKey, crawlspaceObj]) => {
            const { subcomponents: { window = {}, door = {} } = {} } = crawlspaceObj;

            if (!isEmpty(window)) {
                Object.keys(window).forEach((windowKey) => {
                    const { windowType: { codeRef: windowCodeRef = "" } = {} } = window[windowKey];

                    if (windowKey !== windowIgnore && windowCodeRef === editingCodeRef) {
                        accessorsToUpdate = [
                            ...accessorsToUpdate,
                            `model.modelData.components.crawlspace.${crawlspaceKey}.subcomponents.window.${windowKey}`,
                        ];
                    }
                });
            }

            if (!isEmpty(door)) {
                Object.keys(door).forEach((doorKey) => {
                    const { subcomponents: { window: doorWindow = {} } = {} } = door[doorKey];
                    if (!isEmpty(doorWindow)) {
                        Object.keys(doorWindow).forEach((windowKey) => {
                            const { windowType: { codeRef: windowCodeRef = "" } = {} } = doorWindow[windowKey];

                            if (windowKey !== windowIgnore && windowCodeRef === editingCodeRef) {
                                accessorsToUpdate = [
                                    ...accessorsToUpdate,
                                    `model.modelData.components.crawlspace.${crawlspaceKey}.subcomponents.door.${doorKey}.subcomponents.window.${windowKey}`,
                                ];
                            }
                        });
                    }
                });
            }
        });
    }

    return accessorsToUpdate;
};
